import React from "react";
import { Box, Typography } from "@mui/material";

const formatCurrency = (value) => {
  const amount = Number(value);
  return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
};

const getDiscountAmount = (item) => {
  const discountValue = Number(
    item?.discount_value ?? item?.discount_amount ?? item?.discount_percent
  ) || 0;
  if (item?.discount_type === "fixed") return discountValue;
  return (discountValue * (Number(item?.price) || 0)) / 100;
};

const formatConfiguredDiscount = (item) => {
  const value = Number(
    item?.discount_value ?? item?.discount_amount ?? item?.discount_percent
  ) || 0;
  if (item?.discount_type === "percentage") {
    return `${value.toFixed(2).replace(/\.?0+$/, "")}%`;
  }
  return formatCurrency(value);
};

export default function BookingSelectionBreakdown({
  selectedItems = [],
  selectedExtras = [],
  availableExtras = [],
}) {
  const packageRows = selectedItems.filter(Boolean).map((item, index) => ({
    id: `package-${item?.id ?? index}-${index}`,
    type: "Package",
    title: item?.title || "Selected package",
    price: Number(item?.price) || 0,
    discount: getDiscountAmount(item),
    discountLabel: formatConfiguredDiscount(item),
    quantity: 1,
    hours: Number(item?.time_hrs) || 0,
  }));

  const extraRows = selectedExtras.filter(Boolean).map((extra, index) => {
    const details = availableExtras.find(
      (item) => String(item?.id) === String(extra?.extra_id)
    );
    const quantity = Number(extra?.quantity) || 0;

    return {
      id: `extra-${extra?.extra_id ?? index}-${index}`,
      type: "Extra",
      title: details?.title || extra?.title || "Selected extra",
      price: Number(extra?.price) || Number(details?.price) || 0,
      discount: getDiscountAmount(details || extra),
      discountLabel: formatConfiguredDiscount(details || extra),
      quantity,
      hours:
        (Number(extra?.time_hrs) || Number(details?.time_hrs) || 0) * quantity,
    };
  });

  const rows = [...packageRows, ...extraRows];
  const columns = "minmax(90px, 1fr) 58px 62px 28px 38px";

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: "#1e3a5f", mb: 0.75 }}>
        Selection Breakdown
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: columns,
          gap: 0.5,
          pb: 0.65,
          borderBottom: "1px solid #dbe4ee",
          "& span": { color: "#6b7b90", fontSize: "0.64rem", fontWeight: 700 },
        }}
      >
        <span>Selection</span>
        <span>Price</span>
        <span>Discount</span>
        <span>Qty</span>
        <span>Hrs</span>
      </Box>
      {rows.length ? (
        rows.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "grid",
              gridTemplateColumns: columns,
              gap: 0.5,
              py: 0.65,
              alignItems: "center",
              borderBottom: "1px solid #edf1f5",
              "& span": { color: "#24364b", fontSize: "0.68rem" },
            }}
          >
            <Box component="span" sx={{ minWidth: 0 }}>
              <Box
                component="strong"
                title={item.title}
                sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {item.title}
              </Box>
              <Box component="span" sx={{ color: "#7d8da1 !important" }}>
                {item.type}
              </Box>
            </Box>
            <span>{formatCurrency(item.price)}</span>
            <span>{item.discountLabel || formatCurrency(item.discount)}</span>
            <span>{item.quantity}</span>
            <span>{Number(item.hours.toFixed(2))}</span>
          </Box>
        ))
      ) : (
        <Typography sx={{ color: "#7d8da1", fontSize: "0.72rem", py: 1 }}>
          Select a package or extra to see its breakdown.
        </Typography>
      )}
    </Box>
  );
}
